// React and Router
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Project-Specific Constants & Types
import { TireSearchPageCmp, TireSearchPageProps } from "./types";
import { DataWithPagination } from "services/api";
import {
  TireRow,
  TireSearchFormValues,
} from "services/api/calculations/tire/types";
import {
  CALCULATION_TIRE_CREATE_PAGE_TITLE,
  CALCULATION_TIRE_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// Helpers & Utilities
import { transformRoute, useTranslate } from "helpers";

// API Calls
import { getTiresAPI } from "services/api/calculations/tire/api";

// UI Components
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Pagination } from "services/search";

// Table & Navigation Components
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import TabMenu from "../TabMenu/TabMenu";
import TireSearchFilters from "./components/TireSearchFilters/TireSearchFilters";
import TireSearchResults from "./components/TireSearchResults/TireSearchResults";
import TireEditCard from "../TireEditCard/TireEditCard";

// Routing Constants
import { Routes } from "ui/modules/calculation/navigation";
import { initialDataWithPagination } from "services/api/consts";

const TireSearchPage: TireSearchPageCmp = (props: TireSearchPageProps) => {
  const { t } = useTranslate();
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<TireRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [tireId, setTireId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getTiresAPI(pagination, name);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(Routes.CalculationTireDetailPageRouter, {
        tireId: id,
      })
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setTireId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback(
    (searchFormValues: TireSearchFormValues) => {
      fetchSearchResult(searchResults.pagination, searchFormValues.search);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setTireId(null);
    setShowAddNewModal(false);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <PageTitle
              title={CALCULATION_TIRE_SEARCH_PAGE_TITLE.text}
              icon={CALCULATION_TIRE_SEARCH_PAGE_TITLE.icon}
              iconColor={CALCULATION_TIRE_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
          <TabMenu />
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <TireSearchFilters
            onSearchChange={handleSearchChange}
            onAddNew={handleAddNew}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults
              message={t("admin.calculation.setting.tire.search.noresults")}
            />
          ) : (
            <TireSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>
      {showAddNewModal && (
        <Modal show={showAddNewModal} onHide={() => setShowAddNewModal(false)}>
          <ModalHeader
            title={CALCULATION_TIRE_CREATE_PAGE_TITLE.text}
            onHide={() => setShowAddNewModal(false)}
          />
          <ModalBody>
            <TireEditCard tireId={tireId} onSuccess={handleEditSuccess} />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

TireSearchPage.route = Routes.CalculationTireSearchPageRouter;

export default TireSearchPage;
